import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { AdminToolbar } from './admin/AdminToolbar';
import { ProductTable } from './admin/ProductTable';
import { ProductModal } from './admin/ProductModal';
import { DeleteConfirmModal, BulkDeleteModal } from './admin/ConfirmModals';
import { API, CATEGORIES } from './admin/constants';

const devErr = process.env.NODE_ENV !== 'production' ? console.error.bind(console) : () => {};

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterCat, setFilterCat] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [rates, setRates] = useState({ USD: 18.5, GBP: 23.5, EUR: 20.0, AED: 5.0 });
  const [ratesTs, setRatesTs] = useState(null);

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);

  // Delete state
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  // Sync state
  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState('');

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchProducts(); fetchRates(); }, []);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/products?limit=200`, { withCredentials: true });
      setProducts(data.products || data);
    } catch (e) { /* silently handle */
      devErr('[AdminProducts] fetchProducts failed', e);
    }
    setLoading(false);
  };

  const fetchRates = async () => {
    try {
      const { data } = await axios.get(`${API}/currency-rates`);
      setRates(data.rates || {});
      setRatesTs(data.updated_at);
    } catch (e) { /* silently handle */
      devErr('[AdminProducts] fetchRates failed', e);
    }
  };

  const syncCatalog = async () => {
    setSyncing(true);
    setSyncMsg('');
    try {
      const { data } = await axios.post(`${API}/admin/sync-catalog`, {}, { withCredentials: true });
      setSyncMsg(`Catalog synced — ${data.synced_products} products saved.`);
      setTimeout(() => setSyncMsg(''), 5000);
    } catch (e) {
      setSyncMsg(e?.response?.data?.detail || 'Sync failed. Please try again.');
    }
    setSyncing(false);
  };

  const handleDelete = async (pid) => {
    try {
      await axios.delete(`${API}/admin/products/${pid}`, { withCredentials: true });
      setDeleteConfirm(null);
      fetchProducts();
    } catch (e) { /* silently handle */
      devErr('[AdminProducts] handleDelete failed', e);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    setBulkDeleting(true);
    try {
      await axios.post(`${API}/admin/products/bulk-delete`, { product_ids: Array.from(selectedIds) }, { withCredentials: true });
      setSelectedIds(new Set());
      setBulkDeleteConfirm(false);
      fetchProducts();
    } catch (e) { /* silently handle */
      devErr('[AdminProducts] handleBulkDelete failed', e);
    }
    setBulkDeleting(false);
  };

  const toggleSelect = (pid) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(pid)) next.delete(pid); else next.add(pid);
      return next;
    });
  };

  const filtered = products.filter(p => {
    if (!showInactive && !p.is_active) return false;
    if (filterCat && p.category !== filterCat) return false;
    if (search && !`${p.name} ${p.designer}`.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  return (
    <div>
      <AdminToolbar
        search={search} filterCat={filterCat} showInactive={showInactive}
        selectedCount={selectedIds.size} syncing={syncing} syncMsg={syncMsg}
        rates={rates} ratesTs={ratesTs} categories={CATEGORIES}
        onSearchChange={setSearch} onFilterCatChange={setFilterCat}
        onToggleInactive={() => setShowInactive(v => !v)}
        onSync={syncCatalog}
        onBulkDelete={() => setBulkDeleteConfirm(true)}
        onAdd={() => { setEditingProduct(null); setShowModal(true); }}
      />

      <ProductTable
        filtered={filtered} loading={loading} selectedIds={selectedIds}
        onToggleSelect={toggleSelect}
        onToggleSelectAll={() => {
          if (selectedIds.size === filtered.length) setSelectedIds(new Set());
          else setSelectedIds(new Set(filtered.map(p => p.product_id)));
        }}
        onEdit={(p) => { setEditingProduct(p); setShowModal(true); }}
        onDelete={setDeleteConfirm}
      />

      <DeleteConfirmModal
        product={deleteConfirm}
        onConfirm={() => handleDelete(deleteConfirm.product_id)}
        onCancel={() => setDeleteConfirm(null)}
      />

      {bulkDeleteConfirm && (
        <BulkDeleteModal
          count={selectedIds.size} deleting={bulkDeleting}
          onConfirm={handleBulkDelete}
          onCancel={() => setBulkDeleteConfirm(false)}
        />
      )}

      {showModal && (
        <ProductModal
          editingProduct={editingProduct}
          rates={rates}
          onClose={() => setShowModal(false)}
          onSaved={() => { setShowModal(false); fetchProducts(); }}
          syncCatalog={syncCatalog}
        />
      )}
    </div>
  );
}
